/* nt-ideogram-edit.js — route "Edit with AI" through OUR Ideogram API and
   return a UK-imgix URL carrying cs=strip.

   WHY THIS FILE EXISTS (and is not an edit to bysize-v4.js):
   assets/bysize-v4.js is a BUILD ARTIFACT (generated from src/ported/*, whose
   source repo is not in this theme). Editing it is lost on the next rebuild.
   BSV4 exposes a hook instead — it only installs its own AI-edit function when
   one is not already present:

       if (typeof w.nbEditRequest !== "function" && cfg.preview.aiEdit) {
         w.nbEditRequest = (imageUrl, promptText) => nbEdit(imageUrl, promptText, cfg.falKey);
       }

   So defining window.nbEditRequest BEFORE bysize-v4.js runs replaces the AI
   edit path wholesale, and BSV4's fal.ai nano-banana call (with its embedded
   key) is never reached. THIS FILE MUST LOAD BEFORE bysize-v4.js — see
   sections/by-size-v4.liquid.

   WHAT CHANGES vs the old path:
     was: browser -> queue.fal.run/fal-ai/nano-banana/edit  (fal key in the page,
          result served from a fal.ai host)
     now: browser -> bg-removal-proxy Lambda -> api.ideogram.ai/v1/edit-lite
          (Ideogram key stays server-side), Lambda re-hosts the result into the
          UK bucket and returns https://ninjauploads-production-uk.imgix.net/...

   store:"uk" is REQUIRED. Without it the Lambda falls back to US storage
   (ninjauploads-production.imgix.net) — a UK order's art must live in UK
   storage like every other UK upload.

   cs=strip: every image displayed after an AI edit must carry it, and the same
   URL is what lands on the order, so it is appended here at the single point
   the URL is minted. That way preview, tile, cart thumb and the order's print
   file are all the same asset. */
(function () {
  'use strict';

  /* Loaded sitewide from layout/theme.liquid AND (for guaranteed
     before-the-bundle ordering) from sections/by-size-v4.liquid, so make
     installation idempotent. */
  if (window.__ntIdeogramEditInstalled) return;
  window.__ntIdeogramEditInstalled = true;

  /* API Gateway endpoint for the bg-removal/ideogram proxy. CORS on this API
     allows https://ninjatransfers.co.uk (added 2026-08-18); the UK custom
     domain was previously missing, which is why the UK store had been calling
     fal.ai directly instead. */
  var PROXY_URL = 'https://ruhg140mmf.execute-api.us-east-1.amazonaws.com/';
  var STORE = 'uk';

  function withCsStrip(url) {
    if (!url || typeof url !== 'string') return url;
    if (/[?&]cs=/.test(url)) return url;            /* idempotent */
    if (!/imgix\.net/i.test(url)) return url;       /* cs=strip is imgix-only */
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'cs=strip';
  }
  /* Exposed so other surfaces can reuse the exact same rule. */
  window.ntWithCsStrip = window.ntWithCsStrip || withCsStrip;

  /* ─────────────────────────────────────────────────────────────────────
     THE BRIDGE PATCH — this is what actually fixes the by-size "Edit with AI"
     modal. window.nbEditRequest alone was NOT enough:

     assets/bysize-v4-edit.js (the modal in the screenshot) is lazy-injected by
     snippets/bsv4-ai.liquid when the AI tab is opened, and it does NOT consult
     window.nbEditRequest. It destructures its dependencies from the bundle's
     bridge object:

         var { …, nbEdit, … } = window.__bsv4Bridge;      // edit.js line 12
         … await nbEdit(sourceUrl, promptText, cfg.falKey) // line 259

     So it called the bundle's fal nano-banana function directly (giving the
     black background instead of Ideogram's transparent output).

     The bundle assigns window.__bsv4Bridge at boot — AFTER this file runs
     (we load first so the nbEditRequest hook wins) and BEFORE the edit module
     is injected on click. A defineProperty setter therefore catches the
     assignment and swaps nbEdit before any consumer can destructure it, with
     no edit to either build artifact.
     ───────────────────────────────────────────────────────────────────── */
  (function patchBridge() {
    var stored;
    try {
      Object.defineProperty(window, '__bsv4Bridge', {
        configurable: true,
        enumerable: true,
        get: function () { return stored; },
        set: function (v) {
          if (v && typeof v === 'object') {
            try {
              /* Same signature the bundle's nbEdit has (the 3rd arg, falKey,
                 is deliberately ignored — there is no fal call any more). */
              v.nbEdit = function (imageUrl, promptText) {
                return window.nbEditRequest(imageUrl, promptText);
              };
            } catch (e) {}
          }
          stored = v;
        }
      });
    } catch (e) {
      /* If the property is already defined non-configurably, fall back to
         patching in place — better a late patch than none. */
      try {
        if (window.__bsv4Bridge) {
          window.__bsv4Bridge.nbEdit = function (imageUrl, promptText) {
            return window.nbEditRequest(imageUrl, promptText);
          };
        }
      } catch (e2) {}
    }
  })();

  /* ─────────────────────────────────────────────────────────────────────
     BG REMOVAL — Option 3 direct to Ideogram's API.

     bysize-v4-bgr.js looks for an engine hook and, finding none on this store,
     falls back to loading all three options straight off imgix:

         const engine = window.__bsv4BgrRun;
         if (engine) { engine(bare, imgixCand, null, onCandidate).then(res => …) }
         // else: for (const m of BGR_MODELS) { candidateUrl(runUrl, m) … }

     imgix's bg-remove-model=3 is fal-Ideogram, i.e. Option 3 reached Ideogram
     *through* fal. Defining the hook lets Option 3 call OUR Ideogram endpoint
     (api.ideogram.ai/v1/remove-background via the proxy), re-hosted into the UK
     bucket, with cs=strip — while Options 1 and 2 stay exactly as they were.

     Contract: call onCandidate({name, url, persistentUrl}) per option as it
     lands, resolve {all:[…]} in slot order (ix, pc, gram). bysize-v4-bgr.js
     .catch()es this and falls back to imgix-only, so a failure here degrades to
     Option 1 rather than breaking background removal.

     gram is also flipped to imgixServed:false in BGR_MODELS so the order's print
     file comes from this Lambda-hosted PNG (+ trim) rather than an imgix
     model-3 re-render — matching how ninjatransfers.com does it. cs=strip is
     already on the URL we return, and applyBgrProps appends its trim params
     after it, so it survives to the order.
     ───────────────────────────────────────────────────────────────────── */
  window.__bsv4BgrRun = function (bare, imgixCand, _unused, onCandidate) {
    var base = String(bare || '').split('?')[0];
    var landed = [];

    function emit(name, url) {
      var cand = { name: name, url: url, persistentUrl: url };
      landed.push(cand);
      try { if (typeof onCandidate === 'function') onCandidate(cand); } catch (e) {}
    }

    /* Only count an option once its render actually decodes — same validation
       the imgix-native fallback path does with new Image(). */
    function probe(url) {
      return new Promise(function (resolve) {
        var img = new Image();
        img.onload = function () { resolve(true); };
        img.onerror = function () { resolve(false); };
        img.src = url;
      });
    }

    function imgixCandidate(modelParam) {
      return base + '?trim=colorUnlessAlpha&fm=png&w=2000&bg-remove=true' +
        (modelParam ? '&bg-remove-model=' + modelParam : '');
    }

    var jobs = [];

    /* Option 1 — imgix default. Use the exact URL the bundle computed. */
    jobs.push(probe(imgixCand).then(function (ok) { if (ok) emit('ix', imgixCand); }));

    /* Option 2 — imgix model 4 (Pixelcut). Unchanged. */
    var pcUrl = imgixCandidate(4);
    jobs.push(probe(pcUrl).then(function (ok) { if (ok) emit('pc', pcUrl); }));

    /* Option 3 — OUR Ideogram API. Source is a bounded imgix render: the
       Lambda's remove-background path posts the bytes to Ideogram with no
       downscale step of its own, and full-resolution masters have been
       rejected upstream for size. */
    var ideogramSource = base + '?w=2000&fm=png';
    jobs.push(
      fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: 'ideogram', imageUrl: ideogramSource, store: STORE })
      })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) { if (data && data.url) emit('gram', withCsStrip(data.url)); })
        .catch(function () { /* option 3 simply does not appear */ })
    );

    return Promise.all(jobs).then(function () {
      var order = ['ix', 'pc', 'gram'];
      var all = [];
      order.forEach(function (k) {
        for (var i = 0; i < landed.length; i++) {
          if (landed[i].name === k) { all.push(landed[i]); return; }
        }
      });
      return { all: all };
    });
  };

  /* Signature matches what BSV4 would have installed: (imageUrl, promptText)
     -> Promise<string url>. Throwing is correct on failure — the caller shows
     its own error state. */
  window.nbEditRequest = function (imageUrl, promptText) {
    return fetch(PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'ideogram-edit-45g',
        imageUrl: imageUrl,
        prompt: promptText,
        /* AUTO lets Ideogram decide from context, which keeps DTF art on a
           transparent PNG when it should be. */
        transparency: 'AUTO',
        store: STORE
      })
    }).then(function (res) {
      return res.text().then(function (txt) {
        var data = null;
        try { data = JSON.parse(txt); } catch (e) {}
        if (!res.ok) {
          var detail = (data && (data.details || data.error)) || txt.slice(0, 200);
          throw new Error('AI edit failed (' + res.status + '): ' + detail);
        }
        if (!data || !data.url) throw new Error('AI edit response missing url');
        return withCsStrip(data.url);
      });
    });
  };
})();
